'use client';
import React, { useEffect, useRef, useState } from 'react';
import { SAAS_SIGNUP_URL } from '@/lib/saas';

const steps = [
  {
    num: '01',
    title: 'Criar Conta',
    desc: 'Cadastre-se em menos de 2 minutos. Sem cartão de crédito. 14 dias grátis para explorar tudo.',
    icon: '👤',
    color: 'text-primary',
    border: 'border-primary/30',
    bg: 'bg-primary/5',
  },
  {
    num: '02',
    title: 'Configurar Empresa',
    desc: 'Adicione o nome, CNPJ, logo e configure os módulos que sua empresa precisa usar.',
    icon: '🏢',
    color: 'text-secondary',
    border: 'border-secondary/30',
    bg: 'bg-secondary/5',
  },
  {
    num: '03',
    title: 'Cadastrar Produtos',
    desc: 'Importe sua lista de produtos, defina preços, categorias, fotos e controle de estoque.',
    icon: '📦',
    color: 'text-blue-400',
    border: 'border-blue-400/30',
    bg: 'bg-blue-400/5',
  },
  {
    num: '04',
    title: 'Começar a Vender',
    desc: 'Abra o caixa, atenda clientes no PDV, mesas ou cardápio digital. É simples assim.',
    icon: '🚀',
    color: 'text-primary',
    border: 'border-primary/30',
    bg: 'bg-primary/5',
  },
];

export default function HowItWorksSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-24 px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-80 h-80 blob-secondary opacity-20 rounded-full" />
      <div ref={ref} className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-xs font-bold uppercase tracking-widest text-primary mb-6">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Como funciona
          </span>
          <h2 className="text-section-title font-black text-foreground mb-4">
            Comece a usar em{' '}
            <span className="gradient-text">4 passos simples</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Sem instalação, sem complicação. Acesse do computador, tablet ou celular.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {steps?.map((step, i) => (
            <div
              key={step?.num}
              className={`relative transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Connector line */}
              {i < steps?.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-full w-full h-px border-t border-dashed border-border z-0 -translate-y-1/2" style={{ width: 'calc(100% - 3rem)', left: '100%' }} />
              )}

              <div className={`glass-card-hover rounded-2xl p-6 relative z-10 ${step?.bg} border ${step?.border} h-full`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-3xl font-black ${step?.color} opacity-20`}>{step?.num}</span>
                  <span className="text-3xl">{step?.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{step?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step?.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <a
            href={`${SAAS_SIGNUP_URL}?trial=14&utm_source=how-it-works`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-10 py-4 text-base inline-flex items-center gap-2"
          >
            Começar Agora — 14 dias grátis
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}